import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EditorialDashboard = () => {
  const [user, setUser] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [publishedNews, setPublishedNews] = useState([]);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('blog');
  const [viewMode, setViewMode] = useState('drafts'); // 'drafts' or 'published'
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is logged in and is admin
    const storedUser = localStorage.getItem('reporting_user');
    if (!storedUser) {
      navigate('/reporting/login');
      return;
    }

    const userData = JSON.parse(storedUser);
    if (userData.role !== 'admin') {
      alert('Access denied. Admin only.');
      navigate('/reporting/dashboard');
      return;
    }

    setUser(userData);
    loadDrafts();
    loadPublishedNews();
  }, [navigate]);

  const loadDrafts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/reporting/admin/drafts`);
      setDrafts(response.data.drafts);
    } catch (error) {
      console.error('Error loading drafts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPublishedNews = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/reporting/admin/published-news`);
      setPublishedNews(response.data.articles);
    } catch (error) {
      console.error('Error loading published news:', error);
    }
  };

  const handleUnpublish = async (newsId) => {
    if (!window.confirm('Are you sure you want to unpublish this article? It will be removed from the public news page.')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/reporting/admin/news/${newsId}`);
      alert('✅ Article unpublished successfully!');
      loadPublishedNews();
      loadDrafts();
    } catch (error) {
      alert('Error unpublishing article: ' + error.message);
    }
  };

  const handleDeleteDraft = async (draftId) => {
    if (!window.confirm('Are you sure you want to delete this draft? This action cannot be undone.')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/reporting/admin/draft/${draftId}`);
      alert('✅ Draft deleted successfully!');
      loadDrafts();
    } catch (error) {
      alert('Error deleting draft: ' + error.message);
    }
  };

  const handleGenerateContent = async () => {
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;

    const confirmMsg = `Generate monthly content for ${month}/${year}? This will process all weekly reports from this month using AI.`;
    if (!window.confirm(confirmMsg)) return;

    setGenerating(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/reporting/admin/generate-monthly-content?year=${year}&month=${month}`
      );
      
      alert('✅ Monthly content generated successfully!');
      loadDrafts();
    } catch (error) {
      alert('Error generating content: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGenerating(false);
    }
  };

  const loadDraftDetails = async (draftId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/reporting/admin/draft/${draftId}`);
      setSelectedDraft(response.data.draft);
    } catch (error) {
      alert('Error loading draft: ' + error.message);
    }
  };

  const handleApprove = async (draftId) => {
    if (!window.confirm('Approve and publish this content to the public news page?')) return;

    try {
      await axios.post(`${BACKEND_URL}/api/reporting/admin/draft/${draftId}/approve`);
      alert('✅ Content approved and published!');
      setSelectedDraft(null);
      loadDrafts();
    } catch (error) {
      alert('Error approving draft: ' + error.message);
    }
  };

  const handleReject = async (draftId) => {
    const reason = window.prompt('Reason for rejection:');
    if (!reason) return;

    try {
      await axios.post(`${BACKEND_URL}/api/reporting/admin/draft/${draftId}/reject?reason=${encodeURIComponent(reason)}`);
      alert('Draft rejected');
      setSelectedDraft(null);
      loadDrafts();
    } catch (error) {
      alert('Error rejecting draft: ' + error.message);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('reporting_user');
    navigate('/reporting/login');
  };

  const getStatusBadge = (status) => {
    const badges = {
      'pending_review': 'bg-yellow-100 text-yellow-800',
      'approved': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                ✍️ Editorial Dashboard
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Review & Approve Monthly Content
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/reporting/dashboard')}
                className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                ← Coordinator View
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Generate Button & View Toggle */}
        <div className="mb-6 flex justify-between items-center">
          <button
            onClick={handleGenerateContent}
            disabled={generating}
            className="px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {generating ? '🔄 Generating...' : '✨ Generate Monthly Content (AI)'}
          </button>

          {/* View Mode Toggle */}
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('drafts')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                viewMode === 'drafts'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              📝 Drafts ({drafts.length})
            </button>
            <button
              onClick={() => setViewMode('published')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                viewMode === 'published'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              📰 Published ({publishedNews.length})
            </button>
          </div>
        </div>

        {selectedDraft ? (
          /* Draft Details View */
          <div className="bg-white rounded-lg shadow-lg">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {new Date(selectedDraft.generated_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </h2>
                <span className={`inline-block px-3 py-1 mt-2 text-xs font-medium rounded-full ${getStatusBadge(selectedDraft.status)}`}>
                  {selectedDraft.status.replace('_', ' ').toUpperCase()}
                </span>
              </div>
              <button
                onClick={() => setSelectedDraft(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕ Close
              </button>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200">
              <div className="flex gap-4 px-6">
                <button
                  onClick={() => setActiveTab('blog')}
                  className={`py-3 px-4 border-b-2 font-medium text-sm ${
                    activeTab === 'blog'
                      ? 'border-green-500 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  📝 Public Blog Post
                </button>
                <button
                  onClick={() => setActiveTab('internal')}
                  className={`py-3 px-4 border-b-2 font-medium text-sm ${
                    activeTab === 'internal'
                      ? 'border-green-500 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  📊 Internal Report
                </button>
                <button
                  onClick={() => setActiveTab('donor')}
                  className={`py-3 px-4 border-b-2 font-medium text-sm ${
                    activeTab === 'donor'
                      ? 'border-green-500 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  💌 Donor Newsletter
                </button>
              </div>
            </div>

            {/* Content Display */}
            <div className="p-6">
              <div className="prose max-w-none">
                {activeTab === 'blog' && (
                  <div className="whitespace-pre-wrap">{selectedDraft.blog_content}</div>
                )}
                {activeTab === 'internal' && (
                  <div className="whitespace-pre-wrap">{selectedDraft.internal_report}</div>
                )}
                {activeTab === 'donor' && (
                  <div className="whitespace-pre-wrap">{selectedDraft.donor_newsletter}</div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            {selectedDraft.status === 'pending_review' && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex gap-4">
                <button
                  onClick={() => handleApprove(selectedDraft.id)}
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
                >
                  ✅ Approve & Publish
                </button>
                <button
                  onClick={() => handleReject(selectedDraft.id)}
                  className="px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700"
                >
                  ❌ Reject
                </button>
              </div>
            )}
          </div>
        ) : viewMode === 'published' ? (
          /* Published News List */
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Published News Articles ({publishedNews.length})
              </h2>
            </div>

            {publishedNews.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <p>No articles published yet.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {publishedNews.map((article) => (
                  <div
                    key={article.id}
                    className="p-6 hover:bg-gray-50"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900 mb-2">
                          {article.title}
                        </h3>
                        <p className="text-sm text-gray-500">
                          Published: {new Date(article.published_at).toLocaleDateString()}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Views: {article.views || 0} | Month: {article.month}/{article.year}
                        </p>
                      </div>
                      <button
                        onClick={() => handleUnpublish(article.id)}
                        className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
                      >
                        🗑️ Unpublish
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* Drafts List */
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Monthly Drafts ({drafts.length})
              </h2>
            </div>

            {drafts.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <p>No drafts yet.</p>
                <p className="text-sm mt-2">Click "Generate Monthly Content" to create AI-generated content from weekly reports.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {drafts.map((draft) => (
                  <div
                    key={draft.id}
                    className="p-6 hover:bg-gray-50"
                  >
                    <div className="flex justify-between items-start">
                      <div 
                        className="flex-1 cursor-pointer"
                        onClick={() => loadDraftDetails(draft.id)}
                      >
                        <h3 className="font-medium text-gray-900">
                          {new Date(draft.year, draft.month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                          Generated: {new Date(draft.generated_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusBadge(draft.status)}`}>
                          {draft.status.replace('_', ' ').toUpperCase()}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteDraft(draft.id);
                          }}
                          className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                          title="Delete draft"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EditorialDashboard;
